import { useLayoutEffect, useState } from 'react';

/** Where the sticky header has always stuck, and what is used until measured. */
export const DEFAULT_STICKY_TOP = '4.1rem';

/** The fixed app header, as `ConsoleLayout` finds it. */
const HEADER_SELECTOR = 'header, .MuiAppBar-root';

/**
 * A px height in the root's rem, so it scales with the text around it.
 *
 * Exported for its test; the hook is the only caller.
 */
export function toRem(heightPx: number, rootFontPx: number): string | null {
  if (!(heightPx > 0) || !(rootFontPx > 0)) {
    return null;
  }
  return `${Math.round((heightPx / rootFontPx) * 1000) / 1000}rem`;
}

/**
 * The fixed app header's bottom edge, for a sticky element to stop under.
 *
 * The header is content-sized and differs per persona (see `ConsoleLayout`),
 * so the 4.1rem offset this replaces sat about 1.4rem under it — measured
 * 66.5px against 49.2px at a 1440px viewport, 88px against 65.6px at 2000px.
 * Whatever stuck first had its top edge hidden. Measured and kept current
 * instead, in rem, so the root font re-basing at wider viewports moves both.
 */
export function useFixedHeaderClearance(): string {
  const [clearance, setClearance] = useState<string | null>(null);

  useLayoutEffect(() => {
    const header = document.querySelector<HTMLElement>(HEADER_SELECTOR);
    if (!header || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const measure = () => {
      const rootFontPx = Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      setClearance(toRem(header.getBoundingClientRect().bottom, rootFontPx));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    // The root font re-bases with the viewport without resizing the header's
    // box in rem terms, so the window is watched as well.
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return clearance ?? DEFAULT_STICKY_TOP;
}
